import React, { useState, useEffect, useRef } from 'react';
import './TargetedTrendingStyleScriptPanel.css';
import { getAuth } from 'firebase/auth';
import { collection, onSnapshot, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { generateScript } from '../services/apiService';

export default function TargetedTrendingStyleScriptPanel({ 
  open, 
  onClose, 
  targetedVideo,
  onScriptGenerated 
}) {
  const [step, setStep] = useState(1); // 1: Name, 2: Brief Selection (Campaign + Brief), 3: Settings
  const [scriptName, setScriptName] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedBriefId, setSelectedBriefId] = useState('');
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const panelRef = useRef(null);

  // Script settings (no AI style, no video format - always 9:16)
  const [scriptSettings, setScriptSettings] = useState({
    platform: '',
    videoLength: '30',
    creativeControl: 'exact',
    additionalHooks: 0,
    additionalCTAs: 0
  });

  const platforms = [
    { value: 'tiktok', label: 'TikTok', icon: '🎵' },
    { value: 'instagram', label: 'Instagram', icon: '📱' },
    { value: 'meta', label: 'Meta (Facebook)', icon: '👥' },
    { value: 'youtube', label: 'YouTube Shorts', icon: '▶️' }
  ];

  const videoLengths = [
    { value: '30', label: '30 seconds', icon: '⏱️' },
    { value: '60', label: '1 minute', icon: '⏲️' },
    { value: '90', label: '1:30', icon: '⏳' }
  ];

  const creativeControlOptions = [
    { value: 'exact', label: 'Follow script exactly', icon: '📝', description: 'Creators must follow the script word for word' },
    { value: 'some', label: 'Add creative flair', icon: '✨', description: 'Creators can add their style while keeping key points' }
  ];

  // Fetch campaigns when component mounts
  useEffect(() => {
    if (!open) return;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribe = onSnapshot(campaignsRef, (snapshot) => {
      const campaignsList = [];
      snapshot.forEach((doc) => {
        campaignsList.push({ id: doc.id, name: doc.data().name, ...doc.data() });
      });
      setCampaigns(campaignsList);
    }, (error) => {
      console.error('Error fetching campaigns:', error);
    });

    return () => unsubscribe();
  }, [open]);

  // Fetch briefs when campaign is selected
  useEffect(() => {
    if (!selectedCampaign) {
      setBriefs([]);
      setSelectedBriefId('');
      setSelectedBrief(null);
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const briefsRef = collection(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs');
    const unsubscribe = onSnapshot(briefsRef, (snapshot) => {
      const briefsList = [];
      snapshot.forEach((doc) => {
        briefsList.push({ id: doc.id, ...doc.data() });
      });
      setBriefs(briefsList);
    }, (error) => {
      console.error('Error fetching briefs:', error);
    });

    return () => unsubscribe();
  }, [selectedCampaign]);

  const handleCampaignChange = (e) => {
    setSelectedCampaign(e.target.value);
    setSelectedBriefId('');
    setSelectedBrief(null);
  };

  const handleBriefSelect = (e) => {
    const briefId = e.target.value;
    setSelectedBriefId(briefId);
    const brief = briefs.find(b => b.id === briefId);
    setSelectedBrief(brief || null);
  };

  useEffect(() => {
    if (!open) return;
    const resetScroll = () => {
      panelRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    };
    const raf = requestAnimationFrame(resetScroll);
    const timeout = setTimeout(resetScroll, 60);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [open, step]);

  const handleClose = () => {
    // Reset state
    setStep(1);
    setScriptName('');
    setSelectedCampaign('');
    setSelectedBriefId('');
    setSelectedBrief(null);
    setScriptSettings({
      platform: '',
      videoLength: '30',
      creativeControl: 'exact',
      additionalHooks: 0,
      additionalCTAs: 0
    });
    setError('');
    onClose();
  };

  const handleNext = () => {
    setError('');
    
    // Validation
    if (step === 1 && !scriptName.trim()) {
      setError('Please enter a script name');
      return;
    }
    if (step === 2 && !selectedBriefId) {
      setError('Please select a campaign and brief');
      return;
    }
    if (step === 3 && !scriptSettings.platform) {
      setError('Please select a platform');
      return;
    }

    if (step < 3) {
      setStep(step + 1);
    } else {
      handleGenerateScript();
    }
  };

  const handleBack = () => {
    setError('');
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleGenerateScript = async () => {
    setIsLoading(true);
    setError('');

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to generate a script');
      }

      // Prepare script generation data
      const scriptGenerationData = {
        userId: user.uid,
        campaignId: selectedCampaign,
        briefId: selectedBriefId,
        scriptName: scriptName,
        scriptSettings: {
          platform: scriptSettings.platform,
          creativeControl: scriptSettings.creativeControl,
          videoLength: scriptSettings.videoLength,
          videoFormat: '9:16', // Always vertical for targeted trending
          additionalHooks: scriptSettings.additionalHooks,
          additionalCTAs: scriptSettings.additionalCTAs
        },
        briefContext: {
          product: selectedBrief.product || selectedBrief.prompt || '',
          goal: selectedBrief.goal || selectedBrief.campaign_goals || '',
          audience: selectedBrief.audience || selectedBrief.aiResponse || '',
          message: selectedBrief.message || '',
          tone: selectedBrief.tone || '',
          videoStyle: selectedBrief.videoStyle || '',
          requirements: selectedBrief.requirements || ''
        },
        // Pass the targeted trending video's style prompt
        selectedTargetedTrendingVideo: {
          id: targetedVideo.id,
          title: targetedVideo.title,
          stylePrompt: targetedVideo.stylePrompt
        }
      };

      // Get user's auth token for Python API authentication
      const idToken = await user.getIdToken();
      
      // Call the API service with auth token
      const scriptResult = await generateScript(scriptGenerationData, idToken);

      if (scriptResult && scriptResult.result) {
        // Parse the script content
        const scriptContent = parseScriptContent(scriptResult.result);

        // Save script to Firestore with auto-save
        await saveScriptToFirestore(user.uid, selectedCampaign, scriptName, {
          name: scriptName,
          content: scriptContent,
          scriptType: 'targeted_trending',
          targetedVideoId: targetedVideo.id,
          targetedVideoTitle: targetedVideo.title,
          briefId: selectedBrief.id,
          briefName: selectedBrief.name || selectedBrief.id,
          settings: scriptSettings,
          createdAt: new Date().toISOString()
        });

        // Close panel and notify parent to open ClientScriptPanel in edit mode
        handleClose();
        
        if (onScriptGenerated) {
          onScriptGenerated({
            scriptName,
            scriptContent,
            campaignId: selectedCampaign,
            briefId: selectedBrief.id,
            settings: scriptSettings
          });
        }
      } else {
        throw new Error('No script generated');
      }
    } catch (err) {
      console.error('Error generating script:', err);
      setError(err.message || 'Failed to generate script. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const parseScriptContent = (content) => {
    console.log('=== PARSING SCRIPT CONTENT ===');
    console.log('Raw content:', content);
    
    // Parse AI-generated script into structured format
    const lines = [];
    
    // Split by numbered sections (e.g., "1. HOOK", "2. BODY LINE 1", etc.)
    const sections = content.split(/(?=\d+\.\s+[A-Z])/);
    
    console.log('Split sections:', sections);

    sections.forEach((section, index) => {
      if (!section.trim()) return;
      
      console.log(`Processing section ${index}:`, section);
      
      // Determine section type
      const hookMatch = section.match(/HOOK/i);
      const ctaMatch = section.match(/CTA/i);
      const bodyMatch = section.match(/BODY/i);
      
      // Extract Script, Action, and Notes using more flexible regex
      const scriptMatch = section.match(/[-•]\s*Script:\s*([^\n]+(?:\n(?![-•]\s*(?:Action|Notes):)[^\n]+)*)/i);
      const actionMatch = section.match(/[-•]\s*Action:\s*([^\n]+(?:\n(?![-•]\s*Notes:)[^\n]+)*)/i);
      const notesMatch = section.match(/[-•]\s*Notes:\s*([^\n]+(?:\n(?![-•]\s*\w+:)[^\n]+)*)/i);
      
      const scriptText = scriptMatch ? scriptMatch[1].trim() : '';
      const actionText = actionMatch ? actionMatch[1].trim() : '';
      const notesText = notesMatch ? notesMatch[1].trim() : '';
      
      console.log('Extracted:', { scriptText, actionText, notesText });

      if (hookMatch) {
        lines.push({
          type: 'hook',
          text: scriptText,
          action: actionText,
          notes: notesText
        });
      } else if (ctaMatch) {
        lines.push({
          type: 'cta',
          text: scriptText,
          action: actionText,
          notes: notesText
        });
      } else if (bodyMatch || scriptText) {
        // It's a body line
        lines.push({
          type: 'line',
          text: scriptText,
          action: actionText,
          notes: notesText
        });
      }
    });

    console.log('Parsed lines:', lines);

    // If parsing failed, return a default structure with the raw content
    if (lines.length === 0) {
      console.warn('Parsing failed, using default structure');
      return [
        { type: 'hook', text: '', action: '', notes: '' },
        { type: 'line', text: content, action: '', notes: '' },
        { type: 'cta', text: '', action: '', notes: '' }
      ];
    }
    
    return lines;
  };

  const saveScriptToFirestore = async (userId, campaignId, scriptName, scriptData) => {
    try {
      // Scripts are stored as an array inside the brief document, matching ClientScriptPanel's structure
      const briefRef = doc(db, 'users', userId, 'campaigns', campaignId, 'briefs', scriptData.briefId);
      
      // Get current brief to access existing scripts
      const briefDoc = await getDoc(briefRef);
      if (!briefDoc.exists()) {
        throw new Error('Brief not found');
      }
      
      const currentBrief = briefDoc.data();
      const currentScripts = currentBrief.scripts || [];
      
      // Create new script with unique ID
      const newScript = {
        id: Date.now(),
        name: scriptName,
        lines: scriptData.content || [],
        scriptType: scriptData.scriptType,
        targetedVideoId: scriptData.targetedVideoId,
        targetedVideoTitle: scriptData.targetedVideoTitle,
        settings: scriptData.settings,
        assignedBriefIds: [scriptData.briefId],
        createdAt: new Date().toISOString()
      };
      
      // Add new script to the array
      const updatedScripts = [...currentScripts, newScript];
      
      // Update the brief document with the new scripts array
      await updateDoc(briefRef, {
        scripts: updatedScripts,
        updatedAt: new Date().toISOString()
      });
      
      console.log('Script saved successfully to brief');
    } catch (error) {
      console.error('Error saving script:', error);
      throw new Error('Failed to save script: ' + error.message);
    }
  };

  if (!open || !targetedVideo) return null;

  return (
    <div className="targeted-trending-style-panel-overlay">
      <div ref={panelRef} className="targeted-trending-style-panel-container">
        {/* Header */}
        <div className="targeted-trending-style-panel-header">
          <div className="targeted-trending-style-header-content">
            <h2>Create Script Using Targeted Style</h2>
            <div className="targeted-trending-style-video-badge">
              <span className="targeted-trending-style-badge-text">{targetedVideo.title}</span>
            </div>
          </div>
          <button 
            className="targeted-trending-style-close-btn"
            onClick={handleClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="targeted-trending-style-progress">
          {[1, 2, 3].map((stepNum) => (
            <div 
              key={stepNum}
              className={`targeted-trending-style-progress-step ${step >= stepNum ? 'active' : ''} ${step === stepNum ? 'current' : ''}`}
            >
              <div className="targeted-trending-style-progress-circle">{stepNum}</div>
              <span className="targeted-trending-style-progress-label">
                {stepNum === 1 && 'Name'}
                {stepNum === 2 && 'Brief'}
                {stepNum === 3 && 'Settings'}
              </span>
            </div>
          ))}
        </div>

        {/* Content Area */}
        <div className="targeted-trending-style-panel-content">
          {/* Step 1: Script Name */}
          {step === 1 && (
            <div className="targeted-trending-style-step-name">
              <div className="targeted-trending-style-step-header">
                <h2 className="targeted-trending-style-step-title">Name Your Script</h2>
                <p className="targeted-trending-style-step-description">
                  Give your script a descriptive name
                </p>
              </div>
              
              <div className="targeted-trending-style-setup-form">
                <div className="targeted-trending-style-form-group">
                  <label htmlFor="targeted-script-name">Script Name</label>
                  <input
                    id="targeted-script-name"
                    type="text"
                    className="targeted-trending-style-form-input"
                    placeholder="Enter script name (e.g. TikTok Ad #1, Product Demo, etc.)"
                    value={scriptName}
                    onChange={(e) => setScriptName(e.target.value)}
                    disabled={isLoading}
                    maxLength={60}
                  />
                  <div className="targeted-trending-style-form-help">
                    This script name is visible to assigned creators.
                  </div>
                </div>
              </div>

              <div className="targeted-trending-style-step-actions">
                {scriptName.trim() && (
                  <button
                    className="targeted-trending-style-btn-primary"
                    onClick={handleNext}
                    disabled={isLoading}
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Brief Selection (Campaign + Brief) */}
          {step === 2 && (
            <div className="targeted-trending-style-step-brief">
              <div className="targeted-trending-style-step-header">
                <h2 className="targeted-trending-style-step-title">Select a Brief</h2>
                <p className="targeted-trending-style-step-description">
                  Choose which brief you want to create a script for
                </p>
              </div>
              
              <div className="targeted-trending-style-setup-form">
                <div className="targeted-trending-style-form-group">
                  <label htmlFor="targeted-campaign-select">Campaign</label>
                  <select
                    id="targeted-campaign-select"
                    className="targeted-trending-style-form-select"
                    value={selectedCampaign}
                    onChange={handleCampaignChange}
                    disabled={isLoading}
                  >
                    <option value="">Select a campaign...</option>
                    {campaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                    ))}
                  </select>
                </div>
                
                {selectedCampaign && (
                  <div className="targeted-trending-style-form-group">
                    <label htmlFor="targeted-brief-select">Brief</label>
                    <select
                      id="targeted-brief-select"
                      className="targeted-trending-style-form-select"
                      value={selectedBriefId}
                      onChange={handleBriefSelect}
                      disabled={isLoading}
                    >
                      <option value="">Select a brief...</option>
                      {briefs.map(brief => (
                        <option key={brief.id} value={brief.id}>{brief.title || brief.name || brief.campaignName || brief.id}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {selectedBrief && (
                  <div className="targeted-trending-style-selected-brief-preview">
                    <h4>Selected Brief: {selectedBrief.title || selectedBrief.name || selectedBrief.id}</h4>
                    <p><strong>Campaign:</strong> {campaigns.find(c => c.id === selectedCampaign)?.name || selectedCampaign}</p>
                    {(selectedBrief.briefDescription || selectedBrief.product) && (
                      <p><strong>Product:</strong> {selectedBrief.product || selectedBrief.briefDescription}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="targeted-trending-style-step-actions">
                <button
                  className="targeted-trending-style-btn-secondary"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  ← Back
                </button>
                {selectedBriefId && (
                  <button
                    className="targeted-trending-style-btn-primary"
                    onClick={handleNext}
                    disabled={isLoading}
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Script Settings */}
          {step === 3 && (
            <div className="targeted-trending-style-step-settings">
              <div className="targeted-trending-style-step-header">
                <h2 className="targeted-trending-style-step-title">Script Settings</h2>
                <p className="targeted-trending-style-step-description">
                  Customize your script requirements
                </p>
              </div>

              <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: '#2d3748', marginBottom: '24px' }}>Platform & Creative Control</h3>

              <div className="targeted-trending-style-settings-grid">
                {/* Platform */}
                <div className="targeted-trending-style-setting-group targeted-trending-style-platform-selection">
                  <h4 className="targeted-trending-style-section-heading">Select Platform</h4>
                  <div className="targeted-trending-style-button-group">
                    {platforms.map((platform) => (
                      <div
                        key={platform.value}
                        className={`targeted-trending-style-setting-btn ${scriptSettings.platform === platform.value ? 'active' : ''}`}
                        onClick={() => !isLoading && setScriptSettings({ ...scriptSettings, platform: platform.value })}
                        style={{ cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}
                      >
                        <span className="targeted-trending-style-btn-icon">{platform.icon}</span>
                        <span>{platform.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Video Length */}
                <div className="targeted-trending-style-setting-group">
                  <label className="targeted-trending-style-label">Video Length</label>
                  <div className="targeted-trending-style-button-group">
                    {videoLengths.map((length) => (
                      <div
                        key={length.value}
                        className={`targeted-trending-style-setting-btn ${scriptSettings.videoLength === length.value ? 'active' : ''}`}
                        onClick={() => !isLoading && setScriptSettings({ ...scriptSettings, videoLength: length.value })}
                        style={{ cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}
                      >
                        <span className="targeted-trending-style-btn-icon">{length.icon}</span>
                        <span>{length.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Creative Control */}
                <div className="targeted-trending-style-setting-group targeted-trending-style-creative-control-selection">
                  <h4 className="targeted-trending-style-section-heading">Creative Control Level</h4>
                  <div className="targeted-trending-style-button-group">
                    {creativeControlOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`targeted-trending-style-setting-btn ${scriptSettings.creativeControl === option.value ? 'active' : ''}`}
                        onClick={() => !isLoading && setScriptSettings({ ...scriptSettings, creativeControl: option.value })}
                        style={{ cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}
                      >
                        <span className="targeted-trending-style-btn-icon">{option.icon}</span>
                        <span className="targeted-trending-style-btn-label">{option.label}</span>
                        <span className="targeted-trending-style-btn-description">{option.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Hooks */}
                <div className="targeted-trending-style-setting-group">
                  <label className="targeted-trending-style-label">Additional Hooks</label>
                  <div className="targeted-trending-style-plus-minus-row">
                    <button
                      className="targeted-trending-style-plus-minus-btn"
                      type="button"
                      onClick={() => setScriptSettings({ ...scriptSettings, additionalHooks: Math.max(0, scriptSettings.additionalHooks - 1) })}
                      disabled={isLoading || scriptSettings.additionalHooks <= 0}
                    >
                      –
                    </button>
                    <span className="targeted-trending-style-plus-minus-value">{scriptSettings.additionalHooks}</span>
                    <button
                      className="targeted-trending-style-plus-minus-btn"
                      type="button"
                      onClick={() => setScriptSettings({ ...scriptSettings, additionalHooks: Math.min(5, scriptSettings.additionalHooks + 1) })}
                      disabled={isLoading || scriptSettings.additionalHooks >= 5}
                    >
                      +
                    </button>
                  </div>
                  <div className="targeted-trending-style-plus-minus-charge">
                    Extra charge: 0.25 credits per hook<br />
                    {scriptSettings.additionalHooks > 0 && (
                      <span>Total: {(scriptSettings.additionalHooks * 0.25).toFixed(2)} credits</span>
                    )}
                  </div>
                </div>

                {/* Additional CTAs */}
                <div className="targeted-trending-style-setting-group">
                  <label className="targeted-trending-style-label">Additional CTAs</label>
                  <div className="targeted-trending-style-plus-minus-row">
                    <button
                      className="targeted-trending-style-plus-minus-btn"
                      type="button"
                      onClick={() => setScriptSettings({ ...scriptSettings, additionalCTAs: Math.max(0, scriptSettings.additionalCTAs - 1) })}
                      disabled={isLoading || scriptSettings.additionalCTAs <= 0}
                    >
                      –
                    </button>
                    <span className="targeted-trending-style-plus-minus-value">{scriptSettings.additionalCTAs}</span>
                    <button
                      className="targeted-trending-style-plus-minus-btn"
                      type="button"
                      onClick={() => setScriptSettings({ ...scriptSettings, additionalCTAs: Math.min(5, scriptSettings.additionalCTAs + 1) })}
                      disabled={isLoading || scriptSettings.additionalCTAs >= 5}
                    >
                      +
                    </button>
                  </div>
                  <div className="targeted-trending-style-plus-minus-charge">
                    Extra charge: 0.25 credits per CTA<br />
                    {scriptSettings.additionalCTAs > 0 && (
                      <span>Total: {(scriptSettings.additionalCTAs * 0.25).toFixed(2)} credits</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="targeted-trending-style-error">
                  <span className="targeted-trending-style-error-icon">⚠️</span>
                  <span>{error}</span>
                  {!isLoading && (
                    <button
                      className="targeted-trending-style-retry-btn"
                      onClick={() => setError('')}
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              )}

              {/* Step Actions */}
              <div className="targeted-trending-style-step-actions">
                <button
                  className="targeted-trending-style-btn-secondary"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  ← Back
                </button>
                <button
                  className="targeted-trending-style-btn-primary"
                  onClick={handleNext}
                  disabled={isLoading}
                >
                  {isLoading ? 'Generating...' : 'Generate Script'}
                </button>
              </div>
            </div>
          )}

          {/* Loading State - Show when generating */}
          {isLoading && (
            <div className="targeted-trending-style-loading">
              <div className="targeted-trending-style-spinner"></div>
              <p>Analyzing {targetedVideo.title} style and creating your script...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

